import { useEffect, useState } from "react";
import { Button, Modal, Select, useToast } from "@astrum/ui";
import { IMPORT_MAX_BYTES, IMPORT_PRESETS, IMPORT_STATUSES, IMPORT_TARGETS, IMPORT_TARGET_LABEL, IMPORT_UNDO_HOURS, suggestColumns } from "@astrum/shared";
import type { ImportAnalysis, ImportColumnMapping, ImportPresetId, ImportPreview, ImportRecord, ImportStatus } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { choiceValue, hasTitle, mappingPayload, setColumnChoice } from "../lib/import-mapping.js";
import { readUploadResponse, uploadFile } from "../lib/transport.js";
import "./import-tasks-dialog.css";

/**
 * Import tasks from a CSV or Excel file: upload, map columns, preview, import,
 * then (for a day) undo. The file is never stored: every step sends it again.
 */

type Step = "upload" | "mapping" | "preview" | "result";
type Field = { id: string; name: string };
type Analysis = ImportAnalysis & { fields: Field[] };

const STATUS_LABEL: Record<ImportStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  review: "In review",
  done: "Done",
  cancelled: "Cancelled",
};

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** Called after tasks were created or an import was undone, so the page reloads. */
  onChanged: () => void;
}

export function ImportTasksDialog({ open, onClose, projectId, onChanged }: Props) {
  const { toast } = useToast();
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [preset, setPreset] = useState<ImportPresetId>("generic");
  const [columns, setColumns] = useState<ImportColumnMapping[]>([]);
  const [statusMap, setStatusMap] = useState<Record<string, ImportStatus | "">>({});
  const [dateOrder, setDateOrder] = useState<"dmy" | "mdy">("dmy");
  const [estimateUnit, setEstimateUnit] = useState<"minutes" | "hours">("hours");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [skipErrors, setSkipErrors] = useState(false);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [record, setRecord] = useState<ImportRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep("upload");
    setFile(null);
    setAnalysis(null);
    setColumns([]);
    setStatusMap({});
    setPreview(null);
    setRecord(null);
    setSkipErrors(false);
    setAllowDuplicate(false);
    setDuplicate(false);
    setError(null);
  }, [open]);

  /** POST the file (plus text fields) and return the parsed body, or set an error. */
  const send = async <T,>(path: string, fields: Record<string, string> = {}): Promise<T | null> => {
    if (!file) return null;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      for (const [key, value] of Object.entries(fields)) form.append(key, value);
      form.append("file", file);
      const res = await readUploadResponse(await uploadFile(`/projects/${projectId}${path}`, form));
      if (!res.ok) {
        const body = res.body as { error?: string; code?: string };
        if (body.code === "DUPLICATE_IMPORT") setDuplicate(true);
        setError(res.nonJson ? "The server could not take that file. Try a smaller one." : (body.error ?? `Request failed (${res.status})`));
        return null;
      }
      return res.body as T;
    } catch {
      setError("Could not reach the server.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const mappingFields = () => ({ mapping: mappingPayload(columns, statusMap, dateOrder, estimateUnit) });

  const analyse = async () => {
    const res = await send<Analysis>("/import/analyze");
    if (!res) return;
    setAnalysis(res);
    setPreset(res.detectedPreset);
    setColumns(res.suggested);
    setStatusMap(Object.fromEntries(res.statusValues.map((v) => [v, ""])));
    setStep("mapping");
  };

  const applyPreset = (id: ImportPresetId) => {
    if (!analysis) return;
    setPreset(id);
    setColumns(suggestColumns(analysis.headers, id, analysis.fields));
  };

  const runPreview = async () => {
    const res = await send<{ preview: ImportPreview }>("/import/preview", mappingFields());
    if (!res) return;
    setPreview(res.preview);
    setSkipErrors(false);
    setStep("preview");
  };

  const commit = async () => {
    const fields: Record<string, string> = mappingFields();
    if (skipErrors) fields.skipErrors = "1";
    if (allowDuplicate) fields.allowDuplicate = "1";
    const res = await send<{ import: ImportRecord }>("/import", fields);
    if (!res) return;
    setRecord(res.import);
    setStep("result");
    onChanged();
  };

  const undo = async () => {
    if (!record) return;
    setBusy(true);
    try {
      const res = await api.post<{ import: ImportRecord; trashed: number }>(`/projects/${projectId}/imports/${record.id}/undo`);
      setRecord(res.import);
      toast(`Moved ${res.trashed} tasks to the trash`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not undo this import.");
    } finally {
      setBusy(false);
    }
  };

  const pick = (f: File | null) => {
    setError(null);
    setDuplicate(false);
    if (f && f.size > IMPORT_MAX_BYTES) {
      setFile(null);
      setError(`That file is larger than ${Math.round(IMPORT_MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    setFile(f);
  };

  const needsDateOrder = analysis?.sample.some((row) => row.some((c) => /^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}$/.test(c))) ?? false;
  const mappedStatusColumn = columns.some((c) => c.target === "status");

  return (
    <Modal open={open} onClose={onClose} title="Import tasks">
      <div className="import-dlg">
        <ol className="import-dlg__steps" aria-label="Progress">
          {(["upload", "mapping", "preview", "result"] as Step[]).map((s, i) => (
            <li key={s} aria-current={s === step ? "step" : undefined} className={s === step ? "is-current" : undefined}>
              {i + 1}. {{ upload: "File", mapping: "Columns", preview: "Check", result: "Done" }[s]}
            </li>
          ))}
        </ol>

        {step === "upload" && (
          <>
            <p className="import-dlg__hint">
              Choose a CSV or Excel (.xlsx) file, for example an export from Asana or Trello. Up to 5,000 rows and 5 MB.
            </p>
            <input
              type="file"
              accept=".csv,.tsv,.txt,.xlsx"
              aria-label="File to import"
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
            />
            {error && <p className="import-dlg__error" role="alert">{error}</p>}
            <div className="import-dlg__actions">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={analyse} disabled={!file || busy}>{busy ? "Reading…" : "Continue"}</Button>
            </div>
          </>
        )}

        {step === "mapping" && analysis && (
          <>
            <p className="import-dlg__hint">
              {analysis.rowCount} rows in {analysis.fileName}. Choose what each column holds; unmapped columns are ignored.
            </p>
            <Select label="Start from" value={preset} onChange={(e) => applyPreset(e.target.value as ImportPresetId)}>
              {IMPORT_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </Select>
            <div className="import-dlg__scroll">
              <table className="import-dlg__table">
                <thead>
                  <tr><th>Column</th><th>Example</th><th>Imports as</th></tr>
                </thead>
                <tbody>
                  {analysis.headers.map((header, column) => (
                    <tr key={column}>
                      <td>{header}</td>
                      <td className="import-dlg__example">{analysis.sample.find((r) => r[column])?.[column] ?? ""}</td>
                      <td>
                        <Select
                          aria-label={`Maps ${header} to`}
                          value={choiceValue(columns.find((c) => c.column === column))}
                          onChange={(e) => setColumns((cur) => setColumnChoice(cur, column, e.target.value))}
                        >
                          <option value="">Ignore</option>
                          {IMPORT_TARGETS.filter((t) => t !== "custom").map((t) => (
                            <option key={t} value={t}>{IMPORT_TARGET_LABEL[t]}</option>
                          ))}
                          {analysis.fields.map((f) => (
                            <option key={f.id} value={`custom:${f.id}`}>Custom: {f.name}</option>
                          ))}
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!hasTitle(columns) && <p className="import-dlg__error">Choose a column for Title.</p>}

            {mappedStatusColumn && analysis.statusValues.length > 0 && (
              <fieldset className="import-dlg__group">
                <legend>Status values we did not recognise</legend>
                {analysis.statusValues.map((value) => (
                  <Select
                    key={value}
                    label={`"${value}" becomes`}
                    value={statusMap[value] ?? ""}
                    onChange={(e) => setStatusMap((cur) => ({ ...cur, [value]: e.target.value as ImportStatus | "" }))}
                  >
                    <option value="">To do (default)</option>
                    {IMPORT_STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </Select>
                ))}
              </fieldset>
            )}

            <div className="import-dlg__row">
              {needsDateOrder && (
                <Select label="Dates like 03/04/2026 are" value={dateOrder} onChange={(e) => setDateOrder(e.target.value as "dmy" | "mdy")}>
                  <option value="dmy">Day first (3 April)</option>
                  <option value="mdy">Month first (4 March)</option>
                </Select>
              )}
              {columns.some((c) => c.target === "estimate") && (
                <Select label="A plain number in Estimate is" value={estimateUnit} onChange={(e) => setEstimateUnit(e.target.value as "minutes" | "hours")}>
                  <option value="hours">Hours</option>
                  <option value="minutes">Minutes</option>
                </Select>
              )}
            </div>

            {error && <p className="import-dlg__error" role="alert">{error}</p>}
            <div className="import-dlg__actions">
              <Button variant="ghost" onClick={() => setStep("upload")}>Back</Button>
              <Button onClick={runPreview} disabled={!hasTitle(columns) || busy}>{busy ? "Checking…" : "Check the file"}</Button>
            </div>
          </>
        )}

        {step === "preview" && preview && (
          <>
            <p className="import-dlg__summary">
              <strong>{preview.validRows}</strong> of {preview.rowCount} rows are ready to import
              {preview.subtasks > 0 && <>, {preview.subtasks} as subtasks</>}.
              {preview.errorRows > 0 && <> {preview.errorRows} have errors.</>}
            </p>
            {(preview.sectionsToCreate.length > 0 || preview.tagsToCreate.length > 0) && (
              <p className="import-dlg__hint">
                {preview.sectionsToCreate.length > 0 && <>New sections: {preview.sectionsToCreate.join(", ")}. </>}
                {preview.tagsToCreate.length > 0 && <>New tags: {preview.tagsToCreate.join(", ")}.</>}
              </p>
            )}
            {preview.issues.length > 0 && (
              <ul className="import-dlg__issues" aria-label="Problems found">
                {preview.issues.map((issue, i) => (
                  <li key={i} className={`import-dlg__issue import-dlg__issue--${issue.level}`}>
                    <span>Row {issue.row}</span> {issue.message}
                  </li>
                ))}
              </ul>
            )}
            {preview.truncated && <p className="import-dlg__hint">Only the first problems are listed.</p>}
            {preview.errorRows > 0 && (
              <label className="import-dlg__check">
                <input type="checkbox" checked={skipErrors} onChange={(e) => setSkipErrors(e.target.checked)} />
                Skip the {preview.errorRows} rows with errors and import the rest
              </label>
            )}
            {duplicate && (
              <label className="import-dlg__check">
                <input type="checkbox" checked={allowDuplicate} onChange={(e) => setAllowDuplicate(e.target.checked)} />
                I mean to import this file again
              </label>
            )}
            <p className="import-dlg__hint">Imported tasks do not notify assignees or run automations. You can undo for {IMPORT_UNDO_HOURS} hours.</p>
            {error && <p className="import-dlg__error" role="alert">{error}</p>}
            <div className="import-dlg__actions">
              <Button variant="ghost" onClick={() => setStep("mapping")}>Back</Button>
              <Button
                onClick={commit}
                disabled={busy || preview.validRows === 0 || (preview.errorRows > 0 && !skipErrors) || (duplicate && !allowDuplicate)}
              >
                {busy ? "Importing…" : `Import ${preview.validRows} tasks`}
              </Button>
            </div>
          </>
        )}

        {step === "result" && record && (
          <>
            <p className="import-dlg__summary">
              {record.status === "undone" ? (
                <>This import was undone. Its tasks are in the trash.</>
              ) : (
                <><strong>{record.createdCount}</strong> tasks imported{record.skippedCount > 0 && <>, {record.skippedCount} skipped</>}.</>
              )}
            </p>
            {record.status !== "undone" && (record.sectionsCreated > 0 || record.tagsCreated > 0) && (
              <p className="import-dlg__hint">
                Created {record.sectionsCreated} sections and {record.tagsCreated} tags. They stay if you undo.
              </p>
            )}
            {error && <p className="import-dlg__error" role="alert">{error}</p>}
            <div className="import-dlg__actions">
              {record.canUndo && (
                <Button variant="ghost" onClick={undo} disabled={busy}>
                  {busy ? "Undoing…" : "Undo import"}
                </Button>
              )}
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
